<template>
  <div class="min-h-screen bg-gray-50">

    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <div class="flex items-center">
            <i class="fas fa-user-edit text-indigo-500 text-3xl mr-3"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Techniciens de visite</h1>
              <p class="text-sm text-gray-500 mt-1">
                Parmi les lots <strong class="text-indigo-600">à visiter</strong>, détecte ceux dont le technicien attribué diffère du technicien de la dernière visite
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <button
              @click="analyzeIssues"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-search'" class="mr-2"></i>
              {{ loading ? 'Analyse...' : 'Analyser' }}
            </button>

            <button
              v-if="analyzed && fixableIssues.length > 0"
              @click="runDryRun"
              :disabled="fixing"
              class="inline-flex items-center px-4 py-2 border border-indigo-300 text-sm font-medium rounded-md shadow-sm text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fas fa-eye mr-2"></i>
              Simuler tout corriger
            </button>

            <button
              v-if="analyzed && fixableIssues.length > 0"
              @click="showConfirmAll = true"
              :disabled="fixing"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="fixing ? 'fas fa-spinner fa-spin' : 'fas fa-magic'" class="mr-2"></i>
              {{ fixing ? 'Correction...' : `Tout corriger (${fixableIssues.length})` }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <!-- État initial -->
      <div v-if="!analyzed && !loading" class="text-center py-16">
        <i class="fas fa-user-edit text-gray-300 text-6xl mb-4"></i>
        <p class="text-gray-500 text-lg mb-2">Aucune analyse effectuée</p>
        <p class="text-gray-400 text-sm">
          Cliquez sur "Analyser" pour détecter les lots dont le technicien attribué
          diffère du technicien qui a effectué la dernière visite.
        </p>
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="text-center py-16">
        <i class="fas fa-spinner fa-spin text-indigo-500 text-5xl mb-4"></i>
        <p class="text-gray-600">Analyse en cours...</p>
      </div>

      <!-- Erreur -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <div class="flex">
          <i class="fas fa-exclamation-circle text-red-400 text-xl flex-shrink-0 mt-0.5"></i>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Erreur lors de l'analyse</h3>
            <p class="mt-1 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Résultats -->
      <template v-else-if="analyzed">

        <!-- Bandeau synthèse -->
        <div
          class="mb-6 rounded-lg p-4 flex items-center"
          :class="issues.length > 0
            ? 'bg-amber-50 border border-amber-200'
            : 'bg-green-50 border border-green-200'"
        >
          <i
            :class="issues.length > 0
              ? 'fas fa-exclamation-triangle text-amber-500'
              : 'fas fa-check-circle text-green-500'"
            class="text-2xl mr-3 flex-shrink-0"
          ></i>
          <div>
            <p class="font-semibold" :class="issues.length > 0 ? 'text-amber-800' : 'text-green-800'">
              <template v-if="issues.length > 0">
                {{ issues.length }} lot{{ issues.length > 1 ? 's' : '' }} avec discordance détecté{{ issues.length > 1 ? 's' : '' }}
              </template>
              <template v-else>
                Tous les lots sont cohérents — aucune correction nécessaire !
              </template>
            </p>
            <p class="text-sm mt-0.5" :class="issues.length > 0 ? 'text-amber-700' : 'text-green-700'">
              <template v-if="issues.length > 0">
                <template v-if="fixableIssues.length > 0">
                  {{ fixableIssues.length }} lot{{ fixableIssues.length > 1 ? 's' : '' }} corrigeable{{ fixableIssues.length > 1 ? 's' : '' }} — le technicien attribué sera remplacé par celui de la dernière visite.
                </template>
                <template v-if="archivedManagerIssues.length > 0">
                  <span class="inline-flex items-center gap-1 ml-1">
                    <i class="fas fa-ban text-orange-500"></i>
                    {{ archivedManagerIssues.length }} lot{{ archivedManagerIssues.length > 1 ? 's' : '' }} non corrigeable{{ archivedManagerIssues.length > 1 ? 's' : '' }} (technicien archivé).
                  </span>
                </template>
                <template v-if="fixableIssues.length === 0 && archivedManagerIssues.length > 0">
                  Aucune correction possible — tous les techniciens de dernière visite sont archivés.
                </template>
              </template>
              <template v-else>
                Le technicien attribué correspond à la dernière visite sur chaque lot.
              </template>
            </p>
          </div>
        </div>

        <!-- Tableau des discordances -->
        <div v-if="issues.length > 0" class="bg-white rounded-lg shadow overflow-hidden">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                  <input
                    type="checkbox"
                    v-model="selectAll"
                    class="h-4 w-4 text-indigo-600 rounded border-gray-300"
                  />
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lot (NSIL)
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Technicien actuel du lot
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Technicien de la dernière visite
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date dernière visite
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="issue in issues"
                :key="issue.propertyId"
                :class="issue.fixed ? 'bg-green-50 opacity-60' : 'hover:bg-gray-50'"
              >
                <!-- Checkbox -->
                <td class="px-4 py-3">
                  <input
                    type="checkbox"
                    v-model="selectedIds"
                    :value="issue.propertyId"
                    :disabled="issue.fixed || issue.lastVisit?.manager?.enabled === false"
                    class="h-4 w-4 text-indigo-600 rounded border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                </td>

                <!-- NSIL + adresse -->
                <td class="px-4 py-3">
                  <div class="text-sm font-semibold text-gray-900">{{ issue.nsil || '—' }}</div>
                  <div v-if="issue.address" class="text-xs text-gray-500 mt-0.5">{{ issue.address }}</div>
                </td>

                <!-- Technicien actuel -->
                <td class="px-4 py-3">
                  <div v-if="issue.currentManager" class="flex items-center gap-2">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                      <i class="fas fa-times mr-1"></i>
                      {{ issue.currentManager.fullName }}
                    </span>
                  </div>
                  <span v-else class="text-xs text-gray-400 italic">Non attribué</span>
                </td>

                <!-- Technicien de la dernière visite (proposition) -->
                <td class="px-4 py-3">
                  <div v-if="issue.lastVisit.manager" class="flex items-center gap-2 flex-wrap">
                    <!-- Technicien actif → correction possible -->
                    <template v-if="issue.lastVisit.manager.enabled !== false">
                      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        <i class="fas fa-check mr-1"></i>
                        {{ issue.lastVisit.manager.fullName }}
                      </span>
                      <i class="fas fa-arrow-right text-gray-400 text-xs"></i>
                      <span class="text-xs text-gray-600 italic">sera attribué</span>
                    </template>
                    <!-- Technicien archivé → correction impossible -->
                    <template v-else>
                      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                        <i class="fas fa-ban mr-1"></i>
                        {{ issue.lastVisit.manager.fullName }}
                      </span>
                      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                        <i class="fas fa-archive mr-1"></i>
                        Archivé
                      </span>
                    </template>
                  </div>
                  <span v-else class="text-xs text-gray-400 italic">Inconnu</span>
                </td>

                <!-- Date de la dernière visite -->
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-600">{{ formatDate(issue.lastVisit.createdAt) }}</span>
                </td>

                <!-- Action -->
                <td class="px-4 py-3 text-right">
                  <!-- Déjà corrigé -->
                  <span v-if="issue.fixed" class="inline-flex items-center text-xs text-green-600 font-medium">
                    <i class="fas fa-check-circle mr-1"></i> Corrigé
                  </span>
                  <!-- Technicien archivé → correction impossible -->
                  <span
                    v-else-if="issue.lastVisit?.manager?.enabled === false"
                    class="inline-flex items-center text-xs text-orange-600 font-medium"
                    title="Le technicien de la dernière visite est archivé (compte désactivé). Attribuez manuellement un technicien actif à ce lot."
                  >
                    <i class="fas fa-ban mr-1"></i> Non corrigeable
                  </span>
                  <!-- Correction possible -->
                  <button
                    v-else
                    @click="fixSingle(issue)"
                    :disabled="issue.fixing"
                    class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i :class="issue.fixing ? 'fas fa-spinner fa-spin' : 'fas fa-magic'" class="mr-1.5"></i>
                    {{ issue.fixing ? '...' : 'Corriger' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Barre d'action sélection -->
          <div
            v-if="selectedIds.length > 0"
            class="px-6 py-3 bg-indigo-50 border-t border-indigo-200 flex items-center justify-between"
          >
            <span class="text-sm text-indigo-700 font-medium">
              {{ selectedIds.length }} lot{{ selectedIds.length > 1 ? 's' : '' }} sélectionné{{ selectedIds.length > 1 ? 's' : '' }}
            </span>
            <button
              @click="fixSelected"
              :disabled="fixing"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="fixing ? 'fas fa-spinner fa-spin' : 'fas fa-magic'" class="mr-2"></i>
              Corriger la sélection
            </button>
          </div>
        </div>

      </template>

      <!-- Journal des opérations -->
      <div v-if="logs.length > 0" class="mt-8 bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">Journal des opérations</h2>
          <button @click="logs = []" class="text-sm text-gray-500 hover:text-gray-800">Effacer</button>
        </div>
        <div class="px-6 py-4 max-h-72 overflow-y-auto">
          <div v-for="(log, i) in logs" :key="i" class="flex items-start py-1.5 text-sm">
            <span class="text-gray-400 mr-3 flex-shrink-0 font-mono text-xs">{{ log.time }}</span>
            <i
              v-if="log.type === 'success'" class="fas fa-check-circle text-green-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <i
              v-else-if="log.type === 'error'" class="fas fa-times-circle text-red-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <i
              v-else class="fas fa-info-circle text-blue-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <span :class="log.type === 'error' ? 'text-red-700' : 'text-gray-700'">{{ log.message }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- Modal confirmation "Tout corriger" -->
    <div
      v-if="showConfirmAll"
      class="fixed z-50 inset-0 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-50 z-40" @click="showConfirmAll = false"></div>
        <div class="relative bg-white rounded-lg text-left overflow-hidden shadow-xl sm:max-w-lg sm:w-full z-50 mx-4">
          <div class="bg-white px-6 pt-6 pb-4">
            <div class="flex items-start">
              <div class="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
                <i class="fas fa-user-edit text-indigo-600 text-xl"></i>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-medium text-gray-900">Confirmer la correction globale</h3>
                <p class="mt-2 text-sm text-gray-600">
                  Cette opération va mettre à jour le technicien attribué sur
                  <strong>{{ fixableIssues.length }} lot{{ fixableIssues.length > 1 ? 's' : '' }}</strong>
                  en utilisant le technicien de la dernière visite.
                </p>
                <p class="mt-1 text-sm text-gray-600">
                  Le champ <code class="bg-gray-100 px-1 rounded">data_refreshed_at</code> sera également mis à jour
                  pour déclencher la re-synchronisation PWA sur chaque lot modifié.
                </p>
                <p v-if="archivedManagerIssues.length > 0" class="mt-2 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded px-3 py-2">
                  <i class="fas fa-ban mr-1"></i>
                  <strong>{{ archivedManagerIssues.length }} lot{{ archivedManagerIssues.length > 1 ? 's' : '' }}</strong>
                  seront ignorés car leur technicien de dernière visite est archivé.
                </p>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-6 py-3 flex justify-end gap-3">
            <button
              @click="showConfirmAll = false"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
            >
              Annuler
            </button>
            <button
              @click="fixAll"
              class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none"
            >
              <i class="fas fa-magic mr-1.5"></i>
              Corriger {{ fixableIssues.length }} lot{{ fixableIssues.length > 1 ? 's' : '' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import moment from 'moment'

export default {
  name: 'AdminVisitManagers',

  data() {
    return {
      loading: false,
      analyzed: false,
      error: null,
      issues: [],          // liste des discordances
      selectedIds: [],     // lots sélectionnés via checkboxes
      fixing: false,
      showConfirmAll: false,
      logs: [],
    }
  },

  computed: {
    // Issues corrigeables : technicien de la dernière visite actif (enabled !== false)
    fixableIssues() {
      return this.issues.filter(i => !i.fixed && i.lastVisit?.manager?.enabled !== false)
    },
    // Issues non corrigeables : technicien de la dernière visite archivé (enabled === false)
    archivedManagerIssues() {
      return this.issues.filter(i => !i.fixed && i.lastVisit?.manager?.enabled === false)
    },
    selectAll: {
      get() {
        const active = this.fixableIssues
        return active.length > 0 && active.every(i => this.selectedIds.includes(i.propertyId))
      },
      set(val) {
        if (val) {
          this.selectedIds = this.fixableIssues.map(i => i.propertyId)
        } else {
          this.selectedIds = []
        }
      }
    }
  },

  methods: {
    // ── Analyse ───────────────────────────────────────────────────────────────

    async analyzeIssues() {
      this.loading = true
      this.error = null
      this.analyzed = false
      this.issues = []
      this.selectedIds = []
      this.addLog('info', 'Analyse des discordances technicien lot / dernière visite...')

      try {
        const response = await api.getVisitManagerIssues()

        if (!response.success) {
          throw new Error(response.message || response.error || 'Erreur inconnue')
        }

        // Enrichir chaque issue avec des champs réactifs
        this.issues = (response.data || []).map(issue => ({
          ...issue,
          fixing: false,
          fixed: false,
        }))

        this.analyzed = true

        if (this.issues.length > 0) {
          this.addLog('info', `${this.issues.length} lot(s) avec discordance détecté(s)`)
        } else {
          this.addLog('success', 'Aucune discordance détectée — tous les lots sont cohérents !')
        }
      } catch (err) {
        this.error = err.message || 'Impossible d\'analyser les données'
        this.addLog('error', `Erreur : ${this.error}`)
      } finally {
        this.loading = false
      }
    },

    // ── Simulation (dry-run) ──────────────────────────────────────────────────

    async runDryRun() {
      this.addLog('info', `Simulation (dry-run) sur ${this.issues.length} lot(s)...`)
      try {
        const response = await api.fixVisitManagers(
          this.issues.map(i => i.propertyId),
          true // dryRun
        )
        if (response.success) {
          this.addLog('success', `Simulation : ${response.fixed} lot(s) seraient mis à jour. Aucune modification effectuée.`)
        } else {
          this.addLog('error', `Simulation échouée : ${response.message}`)
        }
      } catch (err) {
        this.addLog('error', `Erreur simulation : ${err.message}`)
      }
    },

    // ── Correction d'un seul lot ──────────────────────────────────────────────

    async fixSingle(issue) {
      issue.fixing = true
      this.addLog('info', `Correction du lot ${issue.nsil}...`)

      try {
        const response = await api.fixVisitManagers([issue.propertyId], false)

        if (response.success && response.fixed > 0) {
          issue.fixed = true
          this.selectedIds = this.selectedIds.filter(id => id !== issue.propertyId)
          this.addLog('success', `✓ Lot ${issue.nsil} : ${issue.currentManager?.fullName || 'N/A'} → ${issue.lastVisit.manager?.fullName || 'N/A'} (data_refreshed_at mis à jour)`)
        } else {
          this.addLog('error', `✗ Lot ${issue.nsil} : ${response.message || 'Correction échouée'}`)
        }
      } catch (err) {
        this.addLog('error', `✗ Lot ${issue.nsil} : ${err.message}`)
      } finally {
        issue.fixing = false
      }
    },

    // ── Correction de la sélection ────────────────────────────────────────────

    async fixSelected() {
      if (this.selectedIds.length === 0) return
      this.fixing = true
      this.addLog('info', `Correction de ${this.selectedIds.length} lot(s) sélectionné(s)...`)

      try {
        const response = await api.fixVisitManagers(this.selectedIds, false)

        if (response.success) {
          // Marquer les lots corrigés
          this.issues.forEach(issue => {
            if (this.selectedIds.includes(issue.propertyId)) {
              issue.fixed = true
            }
          })
          this.selectedIds = []
          this.addLog('success', `✅ ${response.fixed} lot(s) corrigé(s) avec succès. Re-sync PWA déclenchée.`)
        } else {
          this.addLog('error', `⚠️ ${response.fixed} succès, ${response.errors?.length} erreur(s)`)
          if (response.errors) {
            response.errors.forEach(e => this.addLog('error', `  ✗ ${e.propertyId} : ${e.reason}`))
          }
        }
      } catch (err) {
        this.addLog('error', `Erreur : ${err.message}`)
      } finally {
        this.fixing = false
      }
    },

    // ── Correction globale ────────────────────────────────────────────────────

    async fixAll() {
      this.showConfirmAll = false
      this.fixing = true
      this.addLog('info', `Correction globale de ${this.issues.length} lot(s)...`)

      try {
        const response = await api.fixVisitManagers(null, false)

        if (response.success || response.fixed > 0) {
          this.issues.forEach(i => { i.fixed = true })
          this.selectedIds = []
          this.addLog('success', `🎉 ${response.fixed} lot(s) corrigé(s) avec succès. Re-sync PWA déclenchée sur tous les lots modifiés.`)
          if (response.errors?.length > 0) {
            response.errors.forEach(e => this.addLog('error', `  ✗ ${e.propertyId} : ${e.reason}`))
          }
        } else {
          this.addLog('error', `Correction échouée : ${response.message}`)
        }
      } catch (err) {
        this.addLog('error', `Erreur : ${err.message}`)
      } finally {
        this.fixing = false
      }
    },

    // ── Utilitaires ───────────────────────────────────────────────────────────

    formatDate(dateStr) {
      if (!dateStr) return '—'
      return moment(dateStr).format('DD/MM/YYYY HH:mm')
    },

    addLog(type, message) {
      this.logs.unshift({
        type,
        message,
        time: new Date().toLocaleTimeString('fr-FR'),
      })
      if (this.logs.length > 100) this.logs = this.logs.slice(0, 100)
    },
  },
}
</script>
